import React, { useState } from "react";
import { toast } from "sonner";
import Drawer from "../../components/drawer/drawer";
import Modal from "../../components/modal/modal";
import PageHeader from "../../components/page-header/page-header";
import { useEffectiveBranchId } from "../../hooks/use-effective-branch-id";
import { Brand } from "../../types";
import "./brands.css";
import { BrandForm, BrandTable } from "./components";
import { useBrands } from "./hooks";
import { BrandManagementState } from "./types/brands-types";
import {
  closeDeleteModal,
  closeDrawer,
  getDrawerTitle,
  openCreateDrawer,
  openDeleteModal,
  openEditDrawer,
  resetBrandState,
} from "./utils/brands-utils";

/**
 * Main Brands page component
 * Manages brand list, creation, editing, and deletion
 */
const Brands: React.FC = () => {
  const [state, setState] = useState<BrandManagementState>(resetBrandState());

  // Get effective branch ID (user's branch for regular users, selected branch for super admin)
  const effectiveBranchId = useEffectiveBranchId();

  const {
    brands,
    isLoading,
    handleCreateBrand,
    handleUpdateBrand,
    handleDeleteBrand,
    // Pagination from hook
    currentPage,
    itemsPerPage,
    totalPages,
    handlePageChange,
    handleItemsPerPageChange,
  } = useBrands(effectiveBranchId);

  // TODO: Improve error handling and add proper response types

  const handleSubmit = async (values: Partial<Brand>): Promise<void> => {
    try {
      let response: any;
      if (state.editingBrand) {
        response = await handleUpdateBrand(state.editingBrand.id, values);
      } else {
        response = await handleCreateBrand(values);
      }

      // Check if response is an error object (error already shown by mutation hook)
      if (response instanceof Error) {
        return; // Don't show duplicate error toast
      }

      // Check if response has explicit success field
      const hasSuccessField = typeof response?.success === "boolean";
      if (hasSuccessField && !response.success) {
        return; // Don't show duplicate error toast
      }

      // Success case
      toast.success(response?.message || "Операция выполнена успешно");
      setState(closeDrawer());
    } catch (error) {
      // Uncaught errors are already handled by mutation hooks
      // Just return to prevent further processing
      return;
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!state.deletingBrand) return;

    await handleDeleteBrand(state.deletingBrand.id, () => {
      toast.success("Бренд успешно удален");
      setState(closeDeleteModal());
    });
  };

  const handleEditDrawer = (brand: Brand): void => {
    setState(openEditDrawer(brand));
  };

  const handleCreateDrawer = (): void => {
    setState(openCreateDrawer());
  };

  const handleCloseDrawer = (): void => {
    setState(closeDrawer());
  };

  const handleDeleteModal = (brand: Brand): void => {
    setState(openDeleteModal(brand));
  };

  const handleCloseDeleteModal = (): void => {
    setState(closeDeleteModal());
  };

  return (
    <div className="brands">
      <PageHeader
        title="Бренды"
        subtitle="Управление брендами автомобилей"
        actionButton={{
          text: "Добавить бренд",
          icon: "plus",
          onClick: handleCreateDrawer,
        }}
      />

      <BrandTable
        brands={brands}
        isLoading={isLoading}
        onEdit={handleEditDrawer}
        onDelete={handleDeleteModal}
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
      />

      <Drawer
        title={getDrawerTitle(state.editingBrand)}
        isOpen={state.isDrawerOpen}
        onClose={handleCloseDrawer}
        width={500}
      >
        <BrandForm
          initialValues={state.editingBrand}
          onSubmit={handleSubmit}
          onCancel={handleCloseDrawer}
          isLoading={isLoading}
        />
      </Drawer>

      <Modal
        isOpen={state.isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        title="Подтвердите удаление"
        onConfirm={handleDelete}
      >
        <p>Вы уверены, что хотите удалить этот бренд?</p>
        <p>
          <strong>{state.deletingBrand?.name}</strong>
        </p>
      </Modal>
    </div>
  );
};

export default Brands;
